import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BlogPost, getBlogPost, getBlogPosts, postUrl } from "./blogService";
import { useBlogPostSeo } from "./useBlogSeo";

const formatDate = (value?: string) =>
  value
    ? new Intl.DateTimeFormat("en", {
        month: "long",
        day: "2-digit",
        year: "numeric",
      }).format(new Date(value))
    : "";

const hasHtmlContent = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

const contactFormHtml = `
  <div class="blog-inline-contact">
    <h3>Need help with your trip?</h3>
    <p>Send your travel details and Al Haramain Cab will help arrange your taxi booking.</p>
    <form action="/contact" method="get">
      <div class="blog-inline-contact-grid">
        <input name="name" type="text" placeholder="Full name" aria-label="Full name" />
        <input name="email" type="email" placeholder="Email address" aria-label="Email address" />
        <input name="phone" type="tel" placeholder="Phone number" aria-label="Phone number" />
      </div>
      <textarea name="message" placeholder="Trip details" aria-label="Trip details"></textarea>
      <button type="submit">Contact Us</button>
    </form>
  </div>
`;

const sanitizeBlogHtml = (value: string) => {
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return value;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(value, "text/html");
  const allowedTags = new Set([
    "A",
    "B",
    "BLOCKQUOTE",
    "BR",
    "DIV",
    "EM",
    "FIGCAPTION",
    "FIGURE",
    "H2",
    "H3",
    "H4",
    "HR",
    "I",
    "IMG",
    "LI",
    "OL",
    "P",
    "SPAN",
    "STRONG",
    "U",
    "UL",
  ]);
  const allowedAttrs = new Set([
    "alt",
    "class",
    "data-blog-contact-form",
    "data-contact-form",
    "height",
    "href",
    "loading",
    "rel",
    "src",
    "target",
    "title",
    "width",
  ]);

  Array.from(doc.body.querySelectorAll("*")).forEach(element => {
    if (!allowedTags.has(element.tagName)) {
      element.replaceWith(document.createTextNode(element.textContent || ""));

      return;
    }

    Array.from(element.attributes).forEach(attribute => {
      if (!allowedAttrs.has(attribute.name)) {
        element.removeAttribute(attribute.name);
      }
    });

    if (element.tagName === "A") {
      const href = element.getAttribute("href") || "";
      const isSafeLink = /^(https?:|mailto:|tel:|\/|#)/i.test(href);
      if (!isSafeLink) element.removeAttribute("href");
      element.setAttribute("rel", "noopener noreferrer");
    }

    if (element.tagName === "IMG") {
      const src = element.getAttribute("src") || "";
      const isSafeImage = /^(https?:|data:image\/|\/)/i.test(src);
      if (!isSafeImage) element.removeAttribute("src");
      element.setAttribute("loading", "lazy");
    }
  });

  return doc.body.innerHTML
    .replace(/<div[^>]*(data-blog-contact-form|data-contact-form)[^>]*>[\s\S]*?<\/div>/gi, contactFormHtml)
    .replace(/\[contact-form\]/gi, contactFormHtml);
};

const RichBlogContent = ({ content }: { content: string }) => {
  if (!hasHtmlContent(content) && !content.includes("[contact-form]")) {
    return (
      <>
        {content.split(/\n{1,}/).map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
      </>
    );
  }

  return <div dangerouslySetInnerHTML={{ __html: sanitizeBlogHtml(content) }} />;
};

const BlogDetails = () => {
  const { slug } = useParams();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  useBlogPostSeo(post);

  useEffect(() => {
    if (!slug) {
      setLoading(false);

      return;
    }

    Promise.all([getBlogPost(slug), getBlogPosts()])
      .then(([selectedPost, posts]) => {
        setPost(selectedPost);
        setRelatedPosts(posts.filter(item => item.slug !== slug).slice(0, 3));
      })
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="main-wrapper">
      <section className="blog-detail-page">
        <div className="container">
          {loading ? (
            <div className="blog-detail-state">Loading blog post...</div>
          ) : !post ? (
            <div className="blog-detail-state">
              <h1>Blog post not found</h1>
              <Link to="/blog" className="blog-detail-back mt-3">
                Back to Blog
              </Link>
            </div>
          ) : (
            <>
              <div className="blog-detail-hero">
                <Link to="/blog" className="blog-detail-back">
                  <i className="feather-arrow-left" />
                  Blog
                </Link>
                <div className="blog-detail-eyebrow">
                  <span>{post.category || "Travel Guide"}</span>
                  <span>{formatDate(post.publishedAt)}</span>
                  <span>{post.author || "Alharamain Cab"}</span>
                </div>
                <h1>{post.title}</h1>
                <p>{post.excerpt}</p>
              </div>

              <div className="row g-4 align-items-start">
                <div className="col-lg-8">
                  <article className="blog-details blog-detail-card">
                    <div className="blog-detail-image">
                    <img
                      src={post.image || "/assets/img/banner.jpg"}
                      alt={post.imageAlt || post.title}
                    />
                  </div>
                    <div className="blog-content">
                      <RichBlogContent content={post.content} />
                    </div>
                    {!!post.tags?.length && (
                      <div className="blog-detail-tags">
                        {post.tags.map(tag => (
                          <span key={tag}>{tag}</span>
                        ))}
                      </div>
                    )}
                  </article>
                </div>

                <aside className="col-lg-4">
                  <div className="blog-detail-sidebar">
                    <div className="blog-sidebar-card blog-booking-card">
                      <span className="blog-sidebar-label">Need transport?</span>
                      <h2>Book a private taxi for your route</h2>
                      <p>Choose a vehicle, confirm pickup details, and travel with Al Haramain Cab support.</p>
                      <Link to="/package" className="blog-sidebar-action">
                        View Packages
                        <i className="feather-arrow-right" />
                      </Link>
                    </div>

                    {!!relatedPosts.length && (
                      <div className="blog-sidebar-card">
                        <h2>More Travel Guides</h2>
                        <div className="blog-related-list">
                          {relatedPosts.map(item => (
                            <Link to={postUrl(item)} key={item.id}>
                              <span>{item.category || "Guide"}</span>
                              <strong>{item.title}</strong>
                              <small>{formatDate(item.publishedAt)}</small>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </aside>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogDetails;
